/**
 * Shop Search Dropdown Web Component
 * ----------------------------------
 * A behavior-only dropdown component for search filters and sorting options.
 * Handles toggle, focus management, and keyboard interactions.
 *
 * The HTML structure (trigger, menu, options) is rendered server-side in HAML.
 * This component only manages:
 * - Click toggle on trigger button
 * - aria-expanded state management
 * - Close on outside click and Escape key
 * - Chevron rotation via CSS
 *
 * Expected HTML structure (server-rendered):
 *   <shop-search-dropdown>
 *     <div class="search-dropdown" data-dropdown>
 *       <button class="search-dropdown__trigger" aria-expanded="false">
 *         Sort by
 *         <svg class="search-dropdown__chevron">...</svg>
 *       </button>
 *       <div class="search-dropdown__menu" hidden>
 *         <a class="search-dropdown__option" href="...">
 *           <span class="search-dropdown__indicator"></span>Option 1
 *         </a>
 *       </div>
 *     </div>
 *   </shop-search-dropdown>
 */

const ShopSearchDropdown = (function () {
  const TAG_NAME = "shop-search-dropdown";

  class ShopSearchDropdown extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      // Defer to next frame — children aren't parsed yet when connectedCallback fires
      requestAnimationFrame(() => this._render());
    }

    _render() {
      if (this._initialized) return;

      this._trigger = this.querySelector(".search-dropdown__trigger");
      this._menu = this.querySelector(".search-dropdown__menu");

      if (!this._trigger || !this._menu) return;

      this._isCheckbox = !!this.querySelector(".search-dropdown--checkbox");
      this._hasChanges = false;

      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);

      this._trigger.addEventListener("click", this._onTriggerClick);
      this._bindExpandButtons();

      if (this._isCheckbox) {
        this._bindCheckboxOptions();
        this._appendApplyButton();
      }

      this._initialized = true;
    }

    _bindExpandButtons() {
      this.querySelectorAll(".search-dropdown__expand").forEach((btn) => {
        btn.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          const group = btn.closest(".search-dropdown__group");
          const children =
            group && group.querySelector(".search-dropdown__children");
          if (!children) return;
          const isExpanded = btn.getAttribute("aria-expanded") === "true";
          btn.setAttribute("aria-expanded", isExpanded ? "false" : "true");
          if (isExpanded) {
            children.setAttribute("hidden", "");
          } else {
            children.removeAttribute("hidden");
          }
        });
      });
    }

    disconnectedCallback() {
      if (this._trigger) {
        this._trigger.removeEventListener("click", this._onTriggerClick);
      }
      document.removeEventListener("click", this._onDocumentClick);
      document.removeEventListener("keydown", this._onKeyDown);
    }

    _onTriggerClick(event) {
      event.stopPropagation();
      const isOpen = this._trigger.getAttribute("aria-expanded") === "true";
      if (isOpen) {
        this._close();
      } else {
        this._open();
      }
    }

    _open() {
      // Close any other open dropdowns first
      document.querySelectorAll("shop-search-dropdown").forEach((other) => {
        if (other !== this && other._initialized) {
          other._close({ skipNavigation: true });
        }
      });
      this._trigger.setAttribute("aria-expanded", "true");
      this._menu.removeAttribute("hidden");
      this._alignMenu();
      document.addEventListener("click", this._onDocumentClick);
      document.addEventListener("keydown", this._onKeyDown);
    }

    _alignMenu() {
      // Align menu left edge with trigger left edge
      this._menu.style.left = this._trigger.offsetLeft + "px";
      this._menu.style.right = "auto";

      // If menu overflows right viewport edge, switch to right-alignment
      const menuRect = this._menu.getBoundingClientRect();
      if (menuRect.right > window.innerWidth) {
        this._menu.style.left = "auto";
        this._menu.style.right = "0";
      }
    }

    _close({ skipNavigation = false } = {}) {
      if (this._isCheckbox && this._hasChanges && !skipNavigation) {
        this._commitPendingNavigation();
      }
      this._trigger.setAttribute("aria-expanded", "false");
      this._menu.setAttribute("hidden", "");
      document.removeEventListener("click", this._onDocumentClick);
      document.removeEventListener("keydown", this._onKeyDown);
    }

    _onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this._close();
      }
    }

    _onKeyDown(event) {
      if (event.key === "Escape") {
        this._hasChanges = false;
        this._close();
        this._trigger.focus();
      }
    }

    _bindCheckboxOptions() {
      this._menu.addEventListener("click", (event) => {
        const option = event.target.closest(".search-dropdown__option");
        if (!option || !option.closest(".search-dropdown__group")) return;

        event.preventDefault();
        option.classList.toggle("search-dropdown__option--active");
        this._hasChanges = true;
        this._updateTriggerText();
      });
    }

    _appendApplyButton() {
      // Wrap existing menu children in a scrollable container so the
      // Apply button stays fixed at the bottom of the dropdown.
      const scrollable = document.createElement("div");
      scrollable.className = "search-dropdown__scroll";
      while (this._menu.firstChild) {
        scrollable.appendChild(this._menu.firstChild);
      }
      this._menu.appendChild(scrollable);

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "search-dropdown__apply";
      btn.textContent = "Apply";
      btn.addEventListener("click", (event) => {
        event.stopPropagation();
        this._applyAndClose();
      });
      this._menu.appendChild(btn);
    }

    _applyAndClose() {
      this._hasChanges = false;
      this._commitPendingNavigation();
      this._close();
    }

    _commitPendingNavigation() {
      window.location.assign(this._buildFilterUrl());
    }

    _buildFilterUrl() {
      const active = this._menu.querySelectorAll(
        ".search-dropdown__group .search-dropdown__option--active",
      );
      const url = new URL(window.location.href);

      [...url.searchParams.keys()]
        .filter((k) => k.startsWith("categories"))
        .forEach((k) => url.searchParams.delete(k));

      active.forEach((opt) => {
        const cat = opt.dataset.category;
        if (cat) url.searchParams.append("categories[]", cat);
      });

      return url.toString();
    }

    _updateTriggerText() {
      const count = this._menu.querySelectorAll(
        ".search-dropdown__group .search-dropdown__option--active",
      ).length;
      const span = this._trigger.querySelector("span");
      if (span) span.textContent = count > 0 ? `${count} selected` : "All";
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopSearchDropdown);
  }

  return ShopSearchDropdown;
})();

export { ShopSearchDropdown };
